import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useCssHandles } from '../../../shared/hooks/useCssHandles'
import { useCart } from '../contexts/CartContext'
import { useAuth } from '../../auth/contexts/AuthContext'
import { createLoginRedirectUrl } from '../../../shared/utils/navigationHelpers'
import { formatPrice } from '../../../shared/utils/checkoutHelpers'
import { Input } from '../../../shared/components'
import { buildImageUrl } from '../../../config'
import { getEffectiveUnitPrice } from '../../../constants/checkout'

const CSS_HANDLES = [
  'minicart',
  'minicartOverlay',
  'minicartContainer',
  'minicartHeader',
  'minicartTitle',
  'minicartClose',
  'minicartContent',
  'minicartItems',
  'minicartItem',
  'itemImage',
  'itemInfo',
  'itemName',
  'itemBrand',
  'itemPrice',
  'itemQuantity',
  'quantityButton',
  'quantityInput',
  'itemRemove',
  'minicartFooter',
  'minicartSubtotal',
  'minicartTotal',
  'minicartActions',
  'viewCartButton',
  'checkoutButton',
  'emptyCart',
  'emptyCartIcon',
  'emptyCartMessage',
  'emptyCartButton'
] as const

/** Considera Campinas para exibir preço com/sem sucata na sidebar. */
const MINICART_ASSUME_CAMPINAS = true

const Minicart: React.FC = () => {
  const handles = useCssHandles(CSS_HANDLES)
  const { state, closeCart, updateQuantity, removeItem, getTotalPrice, getCartItemId, scrapPreference, updateItemScrapPreference } = useCart()
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [imageErrors, setImageErrors] = React.useState<Record<string, boolean>>({})


  const handleImageError = (productId: string) => {
    setImageErrors(prev => ({ ...prev, [productId]: true }))
  }

  const handleQuantityChange = (itemId: string, newQuantity: number) => {
    if (newQuantity < 1) return
    updateQuantity(itemId, newQuantity)
  }

  const handleRemoveItem = (itemId: string) => {
    removeItem(itemId)
  }

  const totalItems = state.items.reduce((total, item) => total + item.quantity, 0)
  const subtotal = getTotalPrice(MINICART_ASSUME_CAMPINAS)

  const handleCheckoutClick = () => {
    closeCart()
    if (isAuthenticated) {
      navigate('/checkout')
      return
    }

    navigate(createLoginRedirectUrl('/checkout'))
  }

  if (!state.isOpen) return null

  return (
    <>
      {/* Overlay */}
      <div
        className={`${handles.minicartOverlay} fixed inset-0 bg-black bg-opacity-50 z-50`}
        onClick={closeCart}
      />

      {/* Minicart */}
      <div className={`${handles.minicart} fixed right-0 top-0 h-full w-96 bg-white shadow-xl z-50 transform transition-transform duration-300`}>
        <div className={`${handles.minicartContainer} h-full flex flex-col`}>
          {/* Header */}
          <div className={`${handles.minicartHeader} flex items-center justify-between p-6 border-b border-gray-200`}>
            <h2 className={`${handles.minicartTitle} text-lg font-semibold text-gray-900`}>
              Carrinho ({totalItems})
            </h2>
            <button
              onClick={closeCart}
              className={`${handles.minicartClose} text-gray-400 hover:text-gray-600`}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className={`${handles.minicartContent} flex-1 overflow-y-auto`}>
            {state.items && state.items.length > 0 ? (
              <div className={`${handles.minicartItems} p-6 space-y-4`}>
                {state.items.map((cartItem) => {
                  const itemId = getCartItemId(cartItem.product)
                  const allowsScrap = (cartItem.product as { allowsScrapReturn?: boolean }).allowsScrapReturn === true
                  const willReturn = cartItem.willReturnScrap ?? scrapPreference ?? true
                  const itemPrice = allowsScrap
                    ? getEffectiveUnitPrice(
                        cartItem.product.price,
                        cartItem.product.weight ?? 0,
                        MINICART_ASSUME_CAMPINAS,
                        willReturn
                      )
                    : cartItem.product.price

                  return (
                    <div key={itemId} className={`${handles.minicartItem} flex space-x-4`}>
                      {/* Imagem */}
                      <div className={`${handles.itemImage} flex-shrink-0`}>
                        {!imageErrors[cartItem.product.id] && cartItem.product.images && cartItem.product.images.length > 0 ? (
                          <img
                            src={
                              cartItem.product.images[0]?.startsWith('http')
                                ? cartItem.product.images[0]
                                : buildImageUrl(cartItem.product.images[0])!
                            }
                            alt={cartItem.product.name}
                            onError={() => handleImageError(cartItem.product.id)}
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                        ) : (
                          <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg">
                            <svg
                              className="w-8 h-8 text-gray-300"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1}
                                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                              />
                            </svg>
                          </div>
                        )}
                      </div>

                      {/* Informações */}
                      <div className={`${handles.itemInfo} flex-1 min-w-0`}>
                        <h3 className={`${handles.itemName} text-sm font-medium text-gray-900 truncate`}>
                          {cartItem.product.name}
                        </h3>
                        <p className={`${handles.itemBrand} text-xs text-gray-500`}>
                          {cartItem.product.brand}
                        </p>
                        <p className={`${handles.itemPrice} text-sm font-semibold text-blue-600 mt-1`}>
                          {formatPrice(itemPrice)}
                        </p>
                        {allowsScrap && (
                          <button
                            type="button"
                            onClick={() => updateItemScrapPreference(cartItem.product.id, !willReturn)}
                            className="text-xs text-blue-600 hover:text-blue-800 underline mt-0.5"
                            aria-label={willReturn ? 'Alterar para sem devolução da sucata' : 'Alterar para com devolução da sucata'}
                          >
                            {willReturn ? 'Com devolução da sucata' : 'Sem devolução da sucata'}
                          </button>
                        )}

                        {/* Quantidade */}
                        <div className={`${handles.itemQuantity} flex items-center mt-2`}>
                          <button
                            onClick={() => handleQuantityChange(itemId, cartItem.quantity - 1)}
                            className={`${handles.quantityButton} w-6 h-6 flex items-center justify-center border border-gray-300 rounded text-gray-600 hover:bg-gray-100`}
                          >
                            -
                          </button>
                          <Input
                            type="number"
                            value={cartItem.quantity}
                            onChange={(e) => handleQuantityChange(itemId, parseInt(e.target.value) || 1)}
                            className={`${handles.quantityInput} w-12 text-center text-sm border-0 focus:outline-none`}
                            min="1"
                          />
                          <button
                            onClick={() => handleQuantityChange(itemId, cartItem.quantity + 1)}
                            className={`${handles.quantityButton} w-6 h-6 flex items-center justify-center border border-gray-300 rounded text-gray-600 hover:bg-gray-100`}
                          >
                            +
                          </button>
                        </div>
                      </div>

                      {/* Remover */}
                      <button
                        onClick={() => handleRemoveItem(itemId)}
                        className={`${handles.itemRemove} text-gray-400 hover:text-red-600`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className={`${handles.emptyCart} flex flex-col items-center justify-center h-full p-6`}>
                <div className={`${handles.emptyCartIcon} text-gray-300 mb-4`}>
                  <svg className="w-24 h-24" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className={`${handles.emptyCartMessage} text-lg font-medium text-gray-900 mb-2`}>
                  Seu carrinho está vazio
                </h3>
                <p className="text-gray-600 text-center mb-6">
                  Confira nosso catálogo completo abaixo!
                </p>
                <Link
                  to="/catalogo"
                  onClick={closeCart}
                  className={`${handles.emptyCartButton} bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors`}
                >
                  Vamos lá!
                </Link>
              </div>
            )}
          </div>

          {/* Footer */}
          {state.items && state.items.length > 0 && (
            <div className={`${handles.minicartFooter} border-t border-gray-200 p-6`}>
              <div className={`${handles.minicartSubtotal} flex justify-between items-center mb-4`}>
                <span className="text-sm text-gray-600">Subtotal:</span>
                <span className="text-lg font-semibold text-gray-900">
                  {formatPrice(subtotal)}
                </span>
              </div>

              <div className={`${handles.minicartTotal} flex justify-between items-center mb-6`}>
                <span className="text-lg font-semibold text-gray-900">Total:</span>
                <span className="text-xl font-bold text-blue-600">
                  {formatPrice(subtotal)}
                </span>
              </div>

              <div className={`${handles.minicartActions} space-y-2`}>
                <button
                  type="button"
                  onClick={handleCheckoutClick}
                  disabled={subtotal < 5}
                  className={`${handles.checkoutButton} w-full bg-blue-600 text-white text-center py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  Finalizar Compra
                </button>
                {subtotal < 5 && (
                  <p className="text-xs text-red-500 text-center">
                    Pedido mínimo de R$ 5,00
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default Minicart

